// src/components/clients/EditarClienteRunningAccountModal.jsx
/**
 * Modal for editing an existing Running Account client.
 *
 * Endpoint: PATCH /apitest/runningaccount/clients/:clientId
 *
 * Pre-populates all fields from the client object passed via `client` prop.
 * This is a full-replace operation — all required fields are always sent.
 *
 * Props:
 *   visible        {boolean}   Whether the modal is open
 *   onClose        {function}  Called when the modal is dismissed
 *   onClientUpdated{function}  Called with the updated client object on success
 *   client         {object}    The running-account client to edit (from GET /clients)
 */
import React, { useState, useEffect } from 'react';
import {
    Modal, Form, Input, Select, Button, message,
    Row, Col, Divider, Alert, Space, Tag, InputNumber
} from 'antd';
import {
    EditOutlined,
    ExclamationCircleOutlined,
    UserOutlined
} from '@ant-design/icons';
import { runningAccountService } from '../../services/runningAccountService';

const { Option } = Select;
const { TextArea } = Input;

// Map API document_type → display label
const DOC_TYPE_OPTIONS = [
    { value: 'CUIT',        label: 'CUIT' },
    { value: 'DNI',         label: 'DNI' },
    { value: 'NO_DOCUMENT', label: 'Sin Documento' }
];

// Tax condition options
const TAX_CONDITIONS = [
    { code: 'CF', label: 'Consumidor Final' },
    { code: 'RI', label: 'Responsable Inscripto' },
    { code: 'M',  label: 'Monotributista' },
    { code: 'EX', label: 'Exento' }
];

const STATUS_OPTIONS = [
    { value: 'ACTIVE',    label: 'Activo',    color: 'success' },
    { value: 'SUSPENDED', label: 'Suspendido', color: 'warning' },
    { value: 'INACTIVE',  label: 'Inactivo',  color: 'default' }
];

/**
 * Derive tax_condition_code from a tax_condition string or tax_condition_code field.
 */
const resolveTaxCode = (client) => {
    if (client.tax_condition_code) return client.tax_condition_code;
    const cond = (client.tax_condition || client.condicion_impositiva || '').toUpperCase();
    if (cond.includes('RESPONSABLE INSCRIPTO') || cond === 'RI') return 'RI';
    if (cond.includes('MONOTRIBUTO') || cond === 'M') return 'M';
    if (cond.includes('EXENTO') || cond === 'EX') return 'EX';
    return 'CF';
};

/**
 * Derive full tax_condition label from code.
 */
const resolveTaxCondition = (client) => {
    if (client.tax_condition) return client.tax_condition;
    const code = resolveTaxCode(client);
    const found = TAX_CONDITIONS.find(t => t.code === code);
    return found ? found.label.toUpperCase() : 'CONSUMIDOR FINAL';
};

const EditarClienteRunningAccountModal = ({ visible, onClose, onClientUpdated, client }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [docTypeRequiresNumber, setDocTypeRequiresNumber] = useState(true);

    // Pre-populate the form whenever the modal opens or the client changes
    useEffect(() => {
        if (visible && client) {
            const docType = client.document_type || 'CUIT';
            setDocTypeRequiresNumber(docType !== 'NO_DOCUMENT');

            form.setFieldsValue({
                document_type:       docType,
                document_number:     client.document_number || '',
                business_name:       client.business_name  || client.razon_social || '',
                display_name:        client.display_name   || client.nombre || client.business_name || '',
                alias:               client.alias          || '',
                tax_condition_code:  resolveTaxCode(client),
                address:             client.address        || client.direccion || '',
                locality:            client.locality       || client.localidad || '',
                province:            client.province       || client.provincia || '',
                phone:               client.phone          || client.telefono || '',
                email:               client.email          || '',
                notes:               client.notes          || client.notas || '',
                credit_limit:        client.credit_limit   != null ? client.credit_limit : null,
                status:              client.status         || 'ACTIVE'
            });
        }

        if (!visible) {
            form.resetFields();
            setDocTypeRequiresNumber(true);
        }
    }, [visible, client]); // eslint-disable-line

    const handleDocTypeChange = (value) => {
        setDocTypeRequiresNumber(value !== 'NO_DOCUMENT');
        if (value === 'NO_DOCUMENT') {
            form.setFieldsValue({ document_number: '' });
        }
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return; // antd shows inline validation errors
        }

        setLoading(true);
        try {
            const taxCode  = values.tax_condition_code || 'CF';
            const taxLabel = TAX_CONDITIONS.find(t => t.code === taxCode)?.label.toUpperCase() || 'CONSUMIDOR FINAL';

            const payload = {
                document_type:      values.document_type,
                document_number:    docTypeRequiresNumber
                    ? (values.document_number || '').replace(/[-\s]/g, '') || null
                    : null,
                business_name:      values.business_name.trim(),
                display_name:       values.display_name.trim(),
                alias:              values.alias?.trim() || null,
                tax_condition:      taxLabel,
                tax_condition_code: taxCode,
                address:            values.address  || null,
                locality:           values.locality || null,
                province:           values.province || null,
                phone:              values.phone    || null,
                email:              values.email    || null,
                notes:              values.notes    || null,
                credit_limit:       values.credit_limit != null ? values.credit_limit : null,
                status:             values.status   || undefined
            };

            const response = await runningAccountService.editClient(client.id, payload);

            if (response.success) {
                message.success('Cliente actualizado exitosamente');
                if (onClientUpdated) {
                    onClientUpdated(response.client);
                }
                onClose();
            } else {
                message.error(response.message || 'Error al actualizar cliente');
            }
        } catch (error) {
            const errData = error?.response?.data;

            if (errData?.error === 'DUPLICATE_DOCUMENT') {
                // Field-level error on the document number input
                form.setFields([{
                    name: 'document_number',
                    errors: [errData.message || 'Otro cliente ya tiene este número de documento']
                }]);
            } else if (errData?.error === 'CLIENT_NOT_FOUND') {
                message.error('El cliente no fue encontrado o fue dado de baja. Recargando lista...');
                onClose();
            } else {
                message.error(errData?.message || 'Error al actualizar cliente');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    const currentStatus = client?.status || 'ACTIVE';
    const statusInfo = STATUS_OPTIONS.find(s => s.value === currentStatus);

    return (
        <Modal
            title={
                <Space>
                    <EditOutlined />
                    <span>Editar Cliente — Cuenta Abierta</span>
                    {statusInfo && (
                        <Tag color={statusInfo.color} style={{ marginLeft: 8 }}>
                            {statusInfo.label}
                        </Tag>
                    )}
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            width={720}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    icon={<EditOutlined />}
                >
                    Guardar Cambios
                </Button>
            ]}
            destroyOnClose
        >
            {client && (
                <Alert
                    message={
                        <span>
                            Editando: <strong>{client.business_name || client.razon_social}</strong>
                            {client.alias && <span style={{ color: '#888', marginLeft: 8 }}>({client.alias})</span>}
                        </span>
                    }
                    description="Todos los campos se reemplazarán al guardar. Los campos opcionales vacíos quedarán en blanco."
                    type="info"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginBottom: 20 }}
                />
            )}

            <Form form={form} layout="vertical" autoComplete="off">
                {/* ── Identification ── */}
                <Divider orientation="left">Identificación</Divider>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="document_type"
                            label="Tipo de Documento"
                            rules={[{ required: true, message: 'Requerido' }]}
                        >
                            <Select onChange={handleDocTypeChange}>
                                {DOC_TYPE_OPTIONS.map(opt => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={16}>
                        <Form.Item
                            name="document_number"
                            label="Número de Documento"
                            rules={[
                                {
                                    required: docTypeRequiresNumber,
                                    message: 'Requerido para este tipo de documento'
                                }
                            ]}
                        >
                            <Input
                                placeholder="20-12345678-9"
                                disabled={!docTypeRequiresNumber}
                                maxLength={20}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="business_name"
                            label="Razón Social"
                            rules={[{ required: true, message: 'La razón social es requerida' }]}
                        >
                            <Input
                                placeholder="Construcciones Pérez e Hijos S.A."
                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="display_name"
                            label="Nombre para mostrar"
                            rules={[{ required: true, message: 'El nombre para mostrar es requerido' }]}
                        >
                            <Input placeholder="Construcciones Pérez" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="alias"
                    label="Alias (opcional)"
                    extra="Apodo comercial corto — también buscable en el selector de clientes"
                >
                    <Input placeholder="Ej: Pérez" maxLength={100} allowClear />
                </Form.Item>

                {/* ── Tax ── */}
                <Divider orientation="left">Condición Impositiva</Divider>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="tax_condition_code"
                            label="Condición Impositiva"
                        >
                            <Select>
                                {TAX_CONDITIONS.map(t => (
                                    <Option key={t.code} value={t.code}>{t.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="credit_limit"
                            label="Límite de Crédito"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                precision={2}
                                prefix="$"
                                placeholder="Sin límite"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* ── Contact ── */}
                <Divider orientation="left">Datos de Contacto</Divider>

                <Form.Item name="address" label="Dirección">
                    <Input placeholder="Av. Colón 1234" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="locality" label="Localidad">
                            <Input placeholder="Córdoba" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="province" label="Provincia">
                            <Input placeholder="Córdoba" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="phone" label="Teléfono">
                            <Input placeholder="0351-4567890" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[{ type: 'email', message: 'Email inválido' }]}
                        >
                            <Input placeholder="email@ejemplo.com" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="notes" label="Notas">
                    <TextArea rows={2} placeholder="Notas adicionales sobre el cliente..." />
                </Form.Item>

                {/* ── Account Status ── */}
                <Divider orientation="left">Estado de Cuenta</Divider>

                <Form.Item
                    name="status"
                    label="Estado"
                    extra="'Suspendido' bloquea nuevos movimientos. 'Inactivo' oculta al cliente de la lista."
                >
                    <Select>
                        {STATUS_OPTIONS.map(s => (
                            <Option key={s.value} value={s.value}>
                                <Tag color={s.color} style={{ marginRight: 6 }}>{s.label}</Tag>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EditarClienteRunningAccountModal;
